import moment from 'moment';
import {
    SearchParamController,
    ResponsiveController,
    PAGINATION_PAGE_PARAM,
    PAGINATION_PER_PAGE_PARAM,
} from '@clockout-dev/design-elements';
import ClockoutController from '../../core/clockout.controller';
import Inherit from '../../core/inherit';
import ReportsDelegate from './reports.delegate';
import ReportsTransactionsDelegate from './reports-transactions.delegate';
import ReportsTransactionsController from './reports-transactions.controller';
import ReportsExportDelegate from './reports-export.delegate';
import ReportsExportController from './reports-export.controller';
import { calculateDateRange } from '../../utils/dates';
import { getLedgerStateFromUrl } from '../../utils/ledger';
import { message } from 'antd';
import {
    DEFAULT_FILTERS,
    DEFAULT_TRANSACTION_FILTERS,
    FILTER_PARAM_KEYS,
    LEGACY_FILTER_PARAM_KEYS,
    SORT_PARAM_KEYS,
    USERS_SORT_FIELDS,
    DEFAULT_PAGE_SIZE,
    SYNC_DETAIL_STATUS,
    SYNC_DETAIL_ERROR,
    FUNNEL_ERROR,
} from './reports.constants';
import { SYNC_REPORT_NONE } from './elements/syncStatus.constants';
import { normalizeFunnelPayload } from './funnelPayload';
import {
    describeFunnelRange,
    detectFunnelMismatch,
    funnelStageChipText,
    isFilterableFunnelCard,
    readFunnelStageParam,
    toggleFunnelStage,
} from './funnelStageFilter';
import { SCOPES } from '../../constants/scopes.constants';
import { resolveSsoBoot } from '../open-sso/resolveSsoBoot';
import { ssoErrorMessage } from '../open-sso/ssoErrorMessage';
import { SSO_EVENTS } from '../../constants/sso.constants';
import { UserActionsDelegate, UserActionsController, USER_ACTIONS } from '../common/userActions';
import SyncStateCatalogController from '../common/statusClassification/syncStateCatalog.controller';

const TAB_SEARCH_PARAM = 'tab';
const VALID_TABS = ['users', 'transactions'];
const DEFAULT_TAB = 'users';

/**
 * The /reports page controller: the Users tab, the funnel, the side panel and
 * tab switching. The Transactions tab is composed in from
 * `ReportsTransactionsController` (+ its delegate), so this file only grows for
 * Users concerns. The shared table's URL reader is a plain function
 * (`getLedgerStateFromUrl` in `utils/ledger`): nothing here inherits the table.
 *
 * Each tab owns its own filter model and its own URL keys (`FILTER_PARAM_KEYS`
 * vs `TRANSACTION_FILTER_PARAM_KEYS`); `handleTabChange` drops the leaving
 * tab's keys so nothing carries across.
 */
class ReportsController extends Inherit(
    ClockoutController,
    UserActionsDelegate,
    UserActionsController,
    ReportsDelegate,
    ReportsTransactionsDelegate,
    ReportsTransactionsController,
    ReportsExportDelegate,
    ReportsExportController,
    SearchParamController,
    ResponsiveController,
    SyncStateCatalogController,
) {

    /**
     * Convenience getter that collapses `isMobile` and `isTablet` into a
     * single "anything smaller than desktop" flag. Drives the responsive
     * choices that share the same threshold (≤1024px):
     *  - `ReportsFilterBar` swaps `Popover` for `Modal`
     *  - The user detail panel is rendered as a full-screen modal instead
     *    of inline in the grid
     *  - Row click opens the panel drawer
     */
    get isSmallScreen() {
        return !!(this.state.isMobile || this.state.isTablet);
    }

    /**
     * What is left of the old catch-all flag: the transactions table's "open
     * ticket" action, whose endpoint is still on admin:team:invite. Every
     * member row action that used to share it (restrict, delete sync, mark
     * complete) has its own scope below.
     */
    get canUserAction() {
        return this.hasScope(SCOPES.TEAM_INVITE);
    }

    /**
     * Restrict / restore a member's access. Its own scope (V15 split it out of
     * admin:team:invite), not canUserAction: an FI can now delegate member
     * lockout without delegating staff invites. Gating both on one flag would
     * show buttons the API rejects, or hide ones it allows.
     */
    get canRestrictUser() {
        return this.hasScope(SCOPES.USERS_RESTRICT);
    }

    get canDeleteSync() {
        return this.hasScope(SCOPES.SYNC_DELETE_LATEST);
    }

    get canMarkSyncComplete() {
        return this.hasScope(SCOPES.SYNC_MARK_COMPLETE);
    }

    get canOpenSso() {
        return this.hasScope(SCOPES.SSO_OPEN);
    }

    get canViewUser() {
        return this.hasScope(SCOPES.DASHBOARD_VIEW);
    }

    /**
     * Resolve the currently selected user object from `state.users` using
     * `state.selectedUserId` as the lookup key. Returns `null` when nothing
     * is selected or the id no longer matches any row (e.g. after a filter
     * change drops the row from the table). Kept on the controller so the
     * view layer can stay declarative and never has to know how selection
     * is stored.
     *
     * @returns {object | null}
     */
    getSelectedUser() {
        const { selectedUserId, users, usersRows } = this.state;
        if (!selectedUserId) return null;
        return (
            (users || []).find((u) => u.id === selectedUserId)
            || (usersRows || []).find((u) => u.id === selectedUserId)
            || null
        );
    }

    /**
     * Whether a consumer account is access-restricted. The backend exposes
     * `account.active === false` as `row.restricted` (it is an account flag,
     * not a journey stage). `restrictedClientIds` holds optimistic overrides
     * until the next fetch.
     *
     * @param {string} [clientId]
     * @param {object} [user]
     * @returns {boolean}
     */
    isUserRestricted(clientId, user) {
        if (!clientId) return false;

        const { restrictedClientIds } = this.state;
        if (restrictedClientIds && Object.prototype.hasOwnProperty.call(restrictedClientIds, clientId)) {
            return !!restrictedClientIds[clientId];
        }

        return (user || this.getUserById(clientId))?.restricted === true;
    }

    /**
     * The loaded row for a client id, from either list. Row actions fire from
     * any row, not just the selected one, so `getSelectedUser()` is the wrong
     * lookup for them — the confirmation names the member, and naming the
     * wrong one (or none) is worse than not asking.
     * @param {string} [clientId]
     * @returns {object|null}
     */
    getUserById(clientId) {
        if (!clientId) return null;
        return (this.state.users || []).find((u) => u.id === clientId)
            || (this.state.usersRows || []).find((u) => u.id === clientId)
            || null;
    }

    /**
     * @param {Array} users
     * @returns {Record<string, boolean>}
     */
    buildRestrictedClientIdsFromUsers(users) {
        const next = {};
        (users || []).forEach((u) => {
            // Explicit false, not an absent key: setUserRestricted stores
            // booleans, and a rebuild that only recorded the restricted ones
            // would silently drop a just-restored member back to whatever
            // the row says. `restricted` is the field the service sends now.
            if (u?.id) next[u.id] = u.restricted === true;
        });
        return next;
    }

    /**
     * Public row-click handler used by `ReportsUsersTable`. Delegates the
     * selection toggle / sync-status load to `ReportsDelegate.selectUser`
     * and then, only on small screens (`isSmallScreen` getter), flips the
     * `panelOpenMobile` flag so the side panel is rendered as a full-screen
     * modal. On desktop the inline split layout already shows the panel,
     * so no extra state is needed.
     *
     * @param {string} clientId
     */
    handleSelectUser(clientId) {
        // Changing (or collapsing) the selection tears down any embedded SSO
        // app so it can't linger over the wrong — or no — user.
        if (this.state.ssoEmbed) {
            this.reportSsoEmbedClosed();
            this.setState({ ssoEmbed: null });
        }
        this.selectUser(clientId);
        if (this.isSmallScreen) {
            this.setState({ panelOpenMobile: true });
        }
    }

    /**
     * Closes the mobile/tablet side-panel modal. Triggered both by the
     * panel's own close button and by the modal chrome (X / Esc / mask
     * click). Intentionally does NOT clear `selectedUserId` — the same row
     * should stay highlighted if the user re-opens the panel on desktop.
     *
     * The modal is `destroyOnHidden`, so closing it unmounts any embedded SSO
     * app along with it — report the close before that embed disappears.
     */
    handleClosePanel() {
        this.reportSsoEmbedClosed();
        this.setState({ panelOpenMobile: false, ssoEmbed: null });
    }

    async initialize() {
        if (!this.user || !this.user.access || this.user.access.length === 0) {
            this.navigateLogin();
            return;
        }

        // Subscribe to the mobile breakpoint as early as possible so the
        // first paint of the filter bar and side panel uses the correct
        // `isMobile` value instead of the `false` placeholder.
        this.mountResponsive();

        // Status colours come from the served classification (KAN-618). Loaded
        // once per session by the data layer; not awaited so the lists never
        // wait on it — pills render neutral until it lands, then repaint.
        this.loadSyncStateCatalog();

        const initialTab = this.resolveInitialTab();
        const searchFromUrl = this.getSearchParamValue(FILTER_PARAM_KEYS.SEARCH) || '';
        const { initialPage, initialPerPage } = this.resolveInitialPagination();
        const initialSort = this.resolveInitialUsersSort();

        if (initialTab === 'transactions') {
            // Filters, sort and page all come from the URL, so a refresh or a
            // shared link lands on the same slice. One reader for page, size
            // and sort: the design system's Table wrote them all.
            const transactionFilters = this.resolveInitialTransactionFilters();
            const { page, perPage, sort } = getLedgerStateFromUrl({ defaultPageSize: DEFAULT_PAGE_SIZE, showFees: true });
            this.setState(
                {
                    user: this.data.user,
                    activeTab: initialTab,
                    transactionFilters,
                    transactionsSort: sort,
                    transactionsSearch: searchFromUrl,
                    transactionsPageInfo: { current: page, perPage, total: 0 },
                },
                () => this.loadTransactions(page, perPage, {
                    filters: transactionFilters, sort, search: searchFromUrl,
                }),
            );
            return;
        }

        const initialFilters = this.resolveInitialFilters();
        this.setState(
            {
                user: this.data.user,
                activeTab: initialTab,
                filters: initialFilters,
                usersSort: initialSort,
                usersSearch: searchFromUrl,
                usersPageInfo: { current: initialPage, perPage: initialPerPage, total: 0 },
            },
            () => {
                this.loadUsers(initialPage, initialPerPage);
                // The funnel is an all-time snapshot: loaded once here, and
                // again only on Refresh Data / the error panel's Retry. Date
                // filters and tab switches deliberately do not refetch it.
                this.loadFunnelMetrics();
            }
        );
    }


    /**
     * Page (common/page) already persists the active tab to `?tab=` via
     * `SearchParamController`, so we just need to mirror that initial value
     * into our own `activeTab` state so the conditional render in `reports.js`
     * shows the correct content on first paint.
     *
     * @returns {string}
     */
    resolveInitialTab() {
        const fromUrl = this.getSearchParamValue(TAB_SEARCH_PARAM);
        const canViewTransactions = this.hasScope(SCOPES.REPORTS_TRANSACTIONS_VIEW);

        if (!canViewTransactions && fromUrl === 'transactions') {
            return DEFAULT_TAB;
        }

        return VALID_TABS.includes(fromUrl) ? fromUrl : DEFAULT_TAB;
    }

    /**
     * Reads `page` / `perPage` from the URL (written by the package Table's
     * `saveSearchParams`) so a refresh or deep-link lands on the same slice.
     *
     * @returns {{ initialPage: number, initialPerPage: number }}
     */
    resolveInitialPagination() {
        const rawPage = parseInt(this.getSearchParamValue(PAGINATION_PAGE_PARAM), 10);
        const rawPerPage = parseInt(this.getSearchParamValue(PAGINATION_PER_PAGE_PARAM), 10);
        return {
            initialPage: Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1,
            initialPerPage: Number.isFinite(rawPerPage) && rawPerPage > 0
                ? rawPerPage
                : DEFAULT_PAGE_SIZE,
        };
    }

    /**
     * Sync our local `activeTab` state with the key surfaced by `Page`'s
     * `onTabChange` callback. The URL write for `tab` was already performed
     * inside `PageController.handleTabChange`. Switching tabs drops BOTH tabs'
     * filter, search, sort and page keys from the URL and resets both filter
     * models, so a Users filter can never leak into a Transactions fetch and
     * vice-versa. `tab` itself stays owned by Page.
     *
     * @param {string} key new active tab key
     */
    handleTabChange(key) {
        if (!VALID_TABS.includes(key)) return;
        if (this.state.activeTab === key) return;

        if (key === 'transactions' && !this.hasScope(SCOPES.REPORTS_TRANSACTIONS_VIEW)) {
            return;
        }

        this.clearFiltersFromUrl();
        this.clearTransactionFiltersFromUrl();
        this.setSearchParamValue(PAGINATION_PAGE_PARAM, null);
        this.reportSsoEmbedClosed();

        const usersPerPage = this.state.usersPageInfo?.perPage ?? DEFAULT_PAGE_SIZE;
        const transactionsPerPage = this.state.transactionsPageInfo?.perPage ?? DEFAULT_PAGE_SIZE;

        this.setState(
            {
                activeTab: key,
                filters: { ...DEFAULT_FILTERS },
                transactionFilters: { ...DEFAULT_TRANSACTION_FILTERS },
                transactionsSort: null,
                usersSort: null,
                usersSearch: '',
                transactionsSearch: '',
                usersPageInfo: { current: 1, perPage: usersPerPage, total: 0 },
                transactionsPageInfo: { current: 1, perPage: transactionsPerPage, total: 0 },
                selectedUserId: null,
                panelOpenMobile: false,
                ssoEmbed: null,
                funnelMismatch: null,
            },
            () => {
                if (key === 'transactions') {
                    this.loadTransactions(1, transactionsPerPage, {
                        filters: { ...DEFAULT_TRANSACTION_FILTERS }, sort: null, search: '',
                    });
                } else {
                    // No funnel refetch on a tab switch: it is an all-time
                    // snapshot, so the answer cannot have changed (KAN-560).
                    this.loadUsers(1, usersPerPage);
                }
            },
        );
    }

    /**
     * Read the four filter URL params (enrollment, ddSwitch, timeFrame,
     * dateStart/dateEnd) and merge them onto DEFAULT_FILTERS so the result
     * is always a valid filter object. Missing keys keep their defaults;
     * dateStart/dateEnd are paired into a `[moment, moment]` tuple that
     * `calculateDateRange` knows how to format.
     *
     * @returns {object}
     */
    resolveInitialFilters() {
        const enrollment = this.getSearchParamValue(FILTER_PARAM_KEYS.ENROLLMENT);
        const ddSwitch = this.getSearchParamValue(FILTER_PARAM_KEYS.DD_SWITCH);
        const timeFrame = this.getSearchParamValue(FILTER_PARAM_KEYS.TIME_FRAME);
        const dateStart = this.getSearchParamValue(FILTER_PARAM_KEYS.DATE_START);
        const dateEnd = this.getSearchParamValue(FILTER_PARAM_KEYS.DATE_END);
        const stage = this.getSearchParamValue(FILTER_PARAM_KEYS.STAGE);
        const payrollStatus = this.getSearchParamValue(FILTER_PARAM_KEYS.PAYROLL_STATUS);
        const funnelCard = this.getSearchParamValue(FILTER_PARAM_KEYS.FUNNEL_CARD);

        // A saved link may still carry `?status=` (pre-KAN-557) or `?payroll=`
        // (pre-KAN-618 placeholder); neither maps onto today's chips, so strip
        // them rather than let the URL claim a filter the chips do not show.
        Object.values(LEGACY_FILTER_PARAM_KEYS).forEach((key) => {
            if (this.getSearchParamValue(key) !== null) this.setSearchParamValue(key, null);
        });

        const next = { ...DEFAULT_FILTERS };

        if (enrollment) next.enrollment = enrollment;
        if (ddSwitch) next.hasCompleteDDSwitch = ddSwitch;
        if (timeFrame) next.timeFrame = timeFrame;
        if (stage && stage !== 'all') next.stage = stage;
        if (payrollStatus && payrollStatus !== 'all') next.payrollStatus = payrollStatus;
        // Only a real card key survives the round-trip (a typo would make the
        // service refuse the request). Whether THIS caller may filter by it is
        // settled when the cards load — see `reconcileFunnelStage`.
        next.funnelStage = readFunnelStageParam(funnelCard);
        if (dateStart && dateEnd) {
            const startM = moment(dateStart, 'YYYY-MM-DD');
            const endM = moment(dateEnd, 'YYYY-MM-DD');
            if (startM.isValid() && endM.isValid()) {
                next.dateRange = [startM, endM];
            }
        }

        return next;
    }

    /**
     * Mirror the current filter state into the URL. Empty / falsy values
     * delete their key (see `SearchParamController.setSearchParamValue`),
     * so the URL stays clean. `dateRange` is serialized as ISO `YYYY-MM-DD`
     * because the API expects that shape and so do moment/dayjs reads.
     *
     * @param {object} filters
     */
    writeFiltersToUrl(filters) {
        const { enrollment, hasCompleteDDSwitch, timeFrame, dateRange, stage, payrollStatus, funnelStage } = filters || {};

        this.setSearchParamValue(FILTER_PARAM_KEYS.ENROLLMENT, enrollment || null);
        this.setSearchParamValue(FILTER_PARAM_KEYS.DD_SWITCH, hasCompleteDDSwitch || null);
        this.setSearchParamValue(FILTER_PARAM_KEYS.TIME_FRAME, timeFrame || null);
        this.setSearchParamValue(FILTER_PARAM_KEYS.STAGE, stage || null);
        this.setSearchParamValue(FILTER_PARAM_KEYS.PAYROLL_STATUS, payrollStatus || null);
        this.setSearchParamValue(FILTER_PARAM_KEYS.FUNNEL_CARD, funnelStage || null);

        const start = dateRange?.[0];
        const end = dateRange?.[1];
        const startStr = start && typeof start.format === 'function' ? start.format('YYYY-MM-DD') : null;
        const endStr = end && typeof end.format === 'function' ? end.format('YYYY-MM-DD') : null;
        this.setSearchParamValue(FILTER_PARAM_KEYS.DATE_START, startStr);
        this.setSearchParamValue(FILTER_PARAM_KEYS.DATE_END, endStr);
    }

    /**
     * Remove every filter URL key but leave the rest of the query string
     * (notably `tab`) untouched. Used by `resetFilters` so reloading after
     * a reset lands on the same tab with the defaults applied.
     */
    clearFiltersFromUrl() {
        [...Object.values(FILTER_PARAM_KEYS), ...Object.values(LEGACY_FILTER_PARAM_KEYS)].forEach((key) => {
            this.setSearchParamValue(key, null);
        });
        Object.values(SORT_PARAM_KEYS).forEach((key) => {
            this.setSearchParamValue(key, null);
        });
    }

    /**
     * Load one page of users using the current filters (or defaults).
     * Updates `usersPageInfo` with the real server total so antd renders
     * the correct page count.
     *
     * Every users fetch — page click, sort, filter change, Refresh — goes
     * through here so ONE generation token guards them all.
     *
     * @param {number} [page]    defaults to current usersPageInfo.current
     * @param {number} [perPage] defaults to current usersPageInfo.perPage
     * @param {{ filters?: object }} [options] filters to apply instead of
     *        `this.state.filters` (a filter change passes the next filters
     *        directly, so it never races the pending setState)
     */
    async loadUsers(page, perPage, { filters } = {}) {
        // Generation token: a sort or page click fires a new load while the
        // previous one is in flight, and only the LAST request may write —
        // otherwise a slow `asc` response can land after the `desc` one and
        // the body contradicts the header arrow and the URL.
        const generation = (this._usersGeneration || 0) + 1;
        this._usersGeneration = generation;
        this.setState({ usersLoading: true, usersFailed: false });
        try {
            const resolvedPage = page ?? this.state.usersPageInfo?.current ?? 1;
            const resolvedPerPage = perPage ?? this.state.usersPageInfo?.perPage ?? DEFAULT_PAGE_SIZE;
            const payload = await this.data.fetchUsers(
                this.buildApiFilters(filters ?? this.state.filters, { page: resolvedPage, perPage: resolvedPerPage }),
            );
            if (this._usersGeneration !== generation) return; // superseded
            // `data.fetchUsers` swallows transport errors (and the service's
            // 400/403/503 card refusals) into `{}` — that is a FAILED request,
            // which must not render as "no users" nor be compared with a card.
            if (!this.isUsersPayload(payload)) {
                this.setState({ usersFailed: true, usersFunnelRange: null, funnelMismatch: null });
                return;
            }
            const next  = this.extractUsers(payload);
            const total = payload?.pageinfo?.count ?? next.length;
            // The service echoes the window it used for the card population
            // (`funnel_range`, all-time today) so the mismatch report can say
            // whether the two numbers were computed over the same window.
            const usersFunnelRange = payload?.funnel_range ?? null;
            this.setState({
                users: next,
                usersRows: next,
                usersFailed: false,
                usersFunnelRange,
                usersPageInfo: { current: resolvedPage, perPage: resolvedPerPage, total },
                usersWarnings: this.extractUsersWarnings(payload),
                restrictedClientIds: this.buildRestrictedClientIdsFromUsers(next),
            }, () => {
                this.reportFunnelMismatch(total, { tableRange: usersFunnelRange });
            });
            // Search is server-side (included in buildApiFilters); no client-side re-filter needed.
        } catch (error) {
            if (this._usersGeneration !== generation) return; // superseded
            console.error('Error loading users:', error);
            this.setState({ usersFailed: true });
        } finally {
            if (this._usersGeneration === generation) {
                this.setState({ usersLoading: false, loading: false });
            }
        }
    }

    /**
     * Loads the 8-card enrollment funnel for the Users report header from
     * `/api/reports/enrollment-funnel/:aba`. The funnel is an all-time
     * snapshot and is deliberately NOT refetched on date-filter changes (the
     * endpoint ignores filter params); it loads on mount, Refresh Data, and
     * the error panel's Retry. Both a thrown fetch and an empty payload end
     * in an explicit error state (FUNNEL_ERROR) instead of a silent unmount.
     *
     * Once the cards are in, the active card (if any) is checked against them:
     * a card this caller may not filter by is dropped (`reconcileFunnelStage`),
     * and the table total we already have is compared with the card's count —
     * this is what makes a deep link with `?card=` (users usually land first)
     * report a mismatch. A response superseded by a newer load is dropped.
     */
    async loadFunnelMetrics() {
        const generation = (this._funnelGeneration || 0) + 1;
        this._funnelGeneration = generation;
        this.setFunnelLoading(true);
        try {
            const payload = await this.data.fetchEnrollmentFunnel();
            if (this._funnelGeneration !== generation) return; // superseded
            const { range, optedIn, cards } = normalizeFunnelPayload(payload);
            this.setFunnel({
                cards,
                range,
                optedIn,
                error: cards.length ? null : { reason: FUNNEL_ERROR.EMPTY },
            });
            if (cards.length && !this.reconcileFunnelStage(cards)) {
                this.reportFunnelMismatch(this.state.usersPageInfo?.total, {
                    funnel: cards,
                    cardRange: range,
                    tableRange: this.state.usersFunnelRange,
                });
            }
        } catch (error) {
            if (this._funnelGeneration !== generation) return; // superseded
            console.error('Error loading funnel metrics:', error);
            this.setFunnel({ error: { reason: FUNNEL_ERROR.LOAD_FAILED } });
        } finally {
            if (this._funnelGeneration === generation) this.setFunnelLoading(false);
        }
    }

    /**
     * `data.fetchUsers` resolves `{}` on any error; a real list always carries
     * `items` (paginated) or `users` (legacy).
     *
     * @param {Object} [payload]
     * @returns {boolean}
     */
    isUsersPayload(payload) {
        return Array.isArray(payload?.items) || Array.isArray(payload?.users);
    }

    /**
     * A deep-linked `?card=` names a card this caller may not filter by (the
     * payload's per-caller `filterable` is false — e.g. First Advance for a
     * non-owner, whom the service would answer with 403). Drop the filter,
     * clean the URL and reload the unfiltered list rather than show a chip
     * the service refuses. Returns true when it did so.
     *
     * @param {Array<{key:string, filterable?:boolean}>} cards
     * @returns {boolean}
     */
    reconcileFunnelStage(cards) {
        const active = this.state.filters?.funnelStage;
        if (!active) return false;
        const card = cards.find((c) => c.key === active);
        if (isFilterableFunnelCard(card)) return false;
        console.warn(`[reports] KPI card "${active}" cannot filter the users list for this user; filter dropped`);
        const nextFilters = { ...this.state.filters, funnelStage: null };
        this.setState({ filters: nextFilters, funnelMismatch: null }, () => this.applyFilters(nextFilters));
        return true;
    }

    /**
     * Normalizes the users API payload. The backend returns paginated
     * `{ items, pageinfo }`; older responses used `{ users: [...] }`.
     *
     * @param {Object} [payload]
     * @returns {Array}
     */
    extractUsers(payload) {
        return payload?.items || payload?.users || [];
    }

    /**
     * `meta.warnings` from the users payload — codes such as
     * `journey_stage_unavailable` / `sync_status_unavailable` that say a
     * secondary source failed while the list still rendered.
     * @param {Object} [payload]
     * @returns {string[]}
     */
    extractUsersWarnings(payload) {
        const warnings = payload?.meta?.warnings;
        return Array.isArray(warnings) ? warnings : [];
    }

    /**
     * Normalizes the ledger API payload (`items` paginated or legacy
     * `transactions`) into a flat transaction row array.
     *
     * @param {Object} [ledger]
     * @returns {Array}
     */
    extractLedgerTransactions(ledger) {
        return ledger?.items || ledger?.transactions || [];
    }

    /**
     * Builds the query object for `fetchUsers` — the USERS tab only. The
     * Transactions tab has `buildTransactionsApiFilters`, which never sends
     * these user attributes.
     *
     * @param {Object} filters
     * @param {{ page?: number, perPage?: number, skipSearch?: boolean }} [options]
     * @returns {Object}
     */
    buildApiFilters(filters, { page, perPage, skipSearch = false } = {}) {
        const { start, end } = calculateDateRange(filters.timeFrame, filters.dateRange);
        const { usersPageInfo, usersSort, usersSearch } = this.state;

        const resolvedPage = page ?? usersPageInfo?.current ?? 1;
        const resolvedPerPage = perPage ?? usersPageInfo?.perPage ?? DEFAULT_PAGE_SIZE;

        const api = {
            enrollment: filters.enrollment,
            hasCompleteDDSwitch: filters.hasCompleteDDSwitch,
            stage: filters.stage,
            payrollStatus: filters.payrollStatus,
            start,
            end,
            page: resolvedPage,
            pageSize: resolvedPerPage,
        };

        // Server-side sort for the users table (whole filtered set, not the page).
        if (usersSort?.by) {
            api.sortBy = usersSort.by;
            api.sortDir = usersSort.dir;
        }

        // The active KPI card: the service returns exactly the population
        // behind it. `stage` above is the row's journey stage — a different
        // filter (a member can be in several cards).
        if (filters.funnelStage) {
            api.funnelStage = filters.funnelStage;
        }

        if (!skipSearch) {
            const term = (usersSearch || '').trim();
            if (term) api.search = term;
        }

        return api;
    }

    /**
     * KPI card click (KAN-583): replace-don't-stack — clicking the active card
     * clears it, clicking another swaps to it; the other filters (stage,
     * payroll, search, date range) stay. Resets to page 1 (state and the URL
     * `page` written by the Table) and re-fetches the table. The cards are an
     * all-time snapshot, so they are not refetched.
     *
     * @param {string} stageKey  card key
     */
    handleFunnelStageClick(stageKey) {
        if (!stageKey || this.state.activeTab !== 'users') return;
        const nextFilters = {
            ...this.state.filters,
            funnelStage: toggleFunnelStage(this.state.filters?.funnelStage ?? null, stageKey),
        };
        this.setSearchParamValue(PAGINATION_PAGE_PARAM, null);
        this.setState(
            {
                filters: nextFilters,
                usersPageInfo: { ...this.state.usersPageInfo, current: 1 },
                funnelMismatch: null,
            },
            () => this.applyFilters(nextFilters),
        );
    }

    /** Remove the active card filter (the chip row's Clear). */
    handleFunnelStageClear() {
        const current = this.state.filters?.funnelStage;
        if (current) this.handleFunnelStageClick(current);
    }

    /**
     * Chips for the Users tab's `ReportsActiveFilters` row — the same
     * component and shape the Transactions tab uses. Only the active KPI card
     * is a chip here: every other Users filter has its own field in the filter
     * bar, and the chip row exists for filters that do not.
     *
     * @param {object} [filters] defaults to state
     * @param {Array<{key:string,label:string}>|null} [funnel] defaults to state
     * @returns {Array<{key: string, label: string}>}
     */
    getActiveUsersFilterChips(filters = this.state.filters, funnel = this.state.funnel) {
        const cardKey = filters?.funnelStage ?? null;
        if (!cardKey) return [];
        return [{ key: 'card', label: funnelStageChipText(cardKey, funnel) }];
    }

    /**
     * KAN-583: when the table total differs from the active card's count, log
     * both numbers and keep a dev badge on the card. The discrepancy is a
     * formula bug to surface, never something to adjust. Callers that hold
     * fresher data than `this.state` (the funnel load, right after setFunnel)
     * pass it in `options` so the check never reads a stale batch.
     *
     * @param {number|null|undefined} total  users `pageinfo.count`
     * @param {{ tableRange?: object|null, funnel?: Array|null, cardRange?: object|null }} [options]
     */
    reportFunnelMismatch(total, { tableRange = null, funnel, cardRange } = {}) {
        // A failed users request has no real total to compare.
        if (this.state.usersFailed) {
            if (this.state.funnelMismatch) this.setState({ funnelMismatch: null });
            return;
        }
        const mismatch = detectFunnelMismatch(
            funnel === undefined ? this.state.funnel : funnel,
            this.state.filters?.funnelStage ?? null,
            total,
            {
                cardRange: cardRange === undefined ? this.state.funnelRange : cardRange,
                tableRange,
            },
        );
        if (mismatch) {
            const window = mismatch.rangeDiffers
                ? `over DIFFERENT windows (cards ${describeFunnelRange(mismatch.cardRange)}, table ${describeFunnelRange(mismatch.tableRange)})`
                : 'for the same window';
            console.warn(
                `[reports] KPI card "${mismatch.stage}" counts ${mismatch.cardCount} but the users table returned ${mismatch.tableCount} ${window}`,
                mismatch,
            );
        }
        if (mismatch || this.state.funnelMismatch) {
            this.setState({ funnelMismatch: mismatch });
        }
    }

    /**
     * Fired by `DebouncedSearch` in the Users tab. Resets to page 1 and
     * re-fetches with the current filters + new term.
     * @param {string} term
     */
    handleUsersSearch(term) {
        this.setState(
            { usersSearch: term || '', usersPageInfo: { ...this.state.usersPageInfo, current: 1 } },
            () => this.applyFilters(),
        );
    }

    /**
     * Fetch users with the current filters and mirror them into the URL.
     * Users tab only — the Transactions tab applies through
     * `applyTransactionFilters`.
     *
     * @param {Object} [filtersOverride] — use when state may not have flushed yet
     */
    async applyFilters(filtersOverride) {
        const filters = filtersOverride ?? this.state.filters;

        this.writeFiltersToUrl(filters);

        // Users only: #127 gave the Transactions tab its own
        // `applyTransactionFilters` in ReportsTransactionsController.
        //
        // Through loadUsers, so this fetch shares the generation token with
        // sorts and page clicks — a slow sort response can no longer overwrite
        // the filtered rows, and this cannot clear a newer load's spinner.
        // loadUsers owns the error path and the loading flags. Filters are
        // passed explicitly because the caller's setState may not have landed yet.
        //
        // The funnel is an all-time snapshot and the endpoint ignores filter
        // params, so it is deliberately NOT refetched here (KAN-560).
        await this.loadUsers(1, this.state.usersPageInfo?.perPage ?? DEFAULT_PAGE_SIZE, { filters });
    }

    /**
     * Reset the Users filters back to defaults, wipe their keys from the URL
     * (keeping `tab` intact) and reload the unfiltered list.
     */
    async resetFilters() {
        this.clearFiltersFromUrl();
        this.setState({
            filters: { ...DEFAULT_FILTERS },
            usersSort: null,
            usersSearch: '',
            // Remounts the uncontrolled search box empty — see ReportsFilterBar.
            usersSearchResetKey: (this.state.usersSearchResetKey || 0) + 1,
            funnelMismatch: null,
            usersFailed: false,
        });

        try {
            // Users only: the Transactions tab resets through
            // `resetTransactionFilters`. The funnel is all-time and is not
            // refetched on a filter reset (KAN-560).
            await this.loadUsers(1, this.state.usersPageInfo?.perPage ?? DEFAULT_PAGE_SIZE);
        } catch (error) {
            console.error('Error resetting filters:', error);
        }
    }

    /**
     * Sort state for the users table: stored, mirrored to the URL
     * (`sortBy` / `sortOrder`, next to the filter params) and applied
     * server-side over the whole filtered set from page 1.
     *
     * @param {{ by: string, dir: 'asc'|'desc' } | null} sort
     */
    setUsersSort(sort) {
        this.writeUsersSortToUrl(sort);
        this.setSearchParamValue(PAGINATION_PAGE_PARAM, null);
        this.setState(
            { usersSort: sort, usersPageInfo: { ...this.state.usersPageInfo, current: 1 } },
            () => this.loadUsers(1, this.state.usersPageInfo?.perPage ?? DEFAULT_PAGE_SIZE),
        );
    }

    /** Reads `sortBy` / `sortOrder` from the URL so a sort survives a refresh. */
    resolveInitialUsersSort() {
        const by = this.getSearchParamValue(SORT_PARAM_KEYS.BY);
        const dir = this.getSearchParamValue(SORT_PARAM_KEYS.DIR);
        if (!by || !USERS_SORT_FIELDS.includes(by)) return null;
        return { by, dir: dir === 'asc' ? 'asc' : 'desc' };
    }

    writeUsersSortToUrl(sort) {
        this.setSearchParamValue(SORT_PARAM_KEYS.BY, sort?.by || null);
        this.setSearchParamValue(SORT_PARAM_KEYS.DIR, sort?.by ? sort.dir : null);
    }

    /**
     * Load the per-user sync detail report for the side panel — the ONLY
     * place the admin calls `…/sync-status`. The table's default render reads
     * the bulk rollup off each row (`record.syncStatus`) and fires nothing.
     *
     * The request is bounded in the data layer (axios timeout, classified as
     * `error.reason`): the panel shows "loading", then either the report or an
     * error with Retry — no indefinite state. A generation counter drops a
     * response that resolves after an admin action (delete / mark complete)
     * already invalidated it. With `patchRow`, the fresh report also updates
     * the row's bulk pill so the table needs no refetch after an action.
     *
     * @param {string} clientId
     * @param {{ force?: boolean, patchRow?: boolean }} [options]
     * @returns {Promise<boolean>} `true` when a report was stored by THIS call
     *          (false: skipped as already loaded, superseded, or failed) — an
     *          action uses it to decide whether its success toast is honest.
     */
    async loadSyncDetailForUser(clientId, { force = false, patchRow = false } = {}) {
        if (!clientId) return false;
        const current = this.state.syncDetailByClientId?.[clientId];
        if (!force && current && (current.status === SYNC_DETAIL_STATUS.READY || current.status === SYNC_DETAIL_STATUS.LOADING)) return false;

        if (!this._syncDetailGeneration) this._syncDetailGeneration = {};
        const generation = (this._syncDetailGeneration[clientId] || 0) + 1;
        this._syncDetailGeneration[clientId] = generation;

        this.setSyncDetail(clientId, { status: SYNC_DETAIL_STATUS.LOADING, report: null, error: null });
        try {
            const payload = await this.data.fetchUserSyncStatus(clientId);
            if (this._syncDetailGeneration[clientId] !== generation) return false; // stale
            // An empty 200 body (older server) is a never-synced member, same as the NONE shell.
            const report = payload || SYNC_REPORT_NONE;
            this.setSyncDetail(clientId, { status: SYNC_DETAIL_STATUS.READY, report, error: null });
            if (patchRow) this.setUserSyncRollup(clientId, this.rollupPatchFromReport(report));
            return true;
        }
        catch (error) {
            if (this._syncDetailGeneration[clientId] !== generation) return false; // stale
            console.error('Error loading sync detail:', error);
            this.setSyncDetail(clientId, {
                status: SYNC_DETAIL_STATUS.ERROR,
                report: null,
                error: error?.reason === SYNC_DETAIL_ERROR.TIMEOUT ? SYNC_DETAIL_ERROR.TIMEOUT : SYNC_DETAIL_ERROR.LOAD_FAILED,
            });
            return false;
        }
    }

    /**
     * The `syncStatus` row fields a fresh detail report is authoritative for.
     * After an admin action the service has persisted the new state, so the
     * stored status equals the displayed one and no retry window is pending.
     * Identifiers and timestamps are copied only when the report carries them
     * (the NONE shell does not), so the cell's "Updated" row is never blanked.
     *
     * @param {object} report
     * @returns {object}
     */
    rollupPatchFromReport(report) {
        const status = report?.rollup_status ?? report?.status ?? 'NONE';
        const patch = { status, storedStatus: status, retryEligibleAt: null };
        const optional = {
            linkTokenId: report?.link_token_id,
            accountId: report?.account_id,
            createdAt: report?.created_at,
            updatedAt: report?.updated_at,
        };
        for (const [key, value] of Object.entries(optional)) {
            if (value != null) patch[key] = value;
        }
        return patch;
    }

    /**
     * Shared tail of the two sync actions: refetch the detail (patching the
     * row pill) and toast honestly — success only when the refetch stored the
     * new state, otherwise say the action went through but the status could
     * not be refreshed (the panel shows Retry, which also patches the row).
     */
    async confirmSyncAction(clientId, successText) {
        const refreshed = await this.loadSyncDetailForUser(clientId, { force: true, patchRow: true });
        if (refreshed) message.success(successText);
        else message.warning(`${successText} The refreshed sync status could not be loaded — use Retry in the panel.`);
    }

    /**
     * Toggle the embedded consumer app (rn-ui) inside the side panel for the
     * given user, impersonating them by clientId. Pressing the button again
     * (same clientId already embedded) tears the app down. The admin is already
     * authenticated, so we just resolve the FI's apiKey + branding + API base
     * (see `resolveSsoBoot`) and hand the boot config to `OpenSsoApp` via the
     * panel. The separate `/open-sso/:clientId` route still powers the
     * user-detail page's Open SSO button.
     * @param {string} clientId
     */
    openSsoForUser(clientId) {
        if (!clientId) return;

        // The button toggles, so this is also the CLOSE path — and closing must
        // not raise an "enter this account?" dialog. Checked before the confirm.
        if (this.state.ssoEmbed?.clientId === clientId) {
            this.exitSsoForUser(clientId);
            return;
        }

        this.requestUserAction(USER_ACTIONS.SSO, clientId, { name: this.getUserById(clientId)?.name });
    }

    /**
     * Leave the embedded session from the identity bar. Distinct entry point
     * from openSsoForUser's toggle so the call site reads as what it is, but the
     * same teardown: report the close, unmount the embed.
     * @param {string} clientId
     */
    exitSsoForUser(clientId) {
        if (!clientId || this.state.ssoEmbed?.clientId !== clientId) return;
        this.reportSsoEmbedClosed();
        this.setState({ ssoEmbed: null });
    }

    /**
     * Reports `session_closed` for whatever embed is currently mounted, if any.
     * Every path that tears the embed down without going through the identity
     * bar's Exit button (switching rows or tabs, closing the mobile panel,
     * unmounting Reports) must call this before clearing `ssoEmbed` — otherwise
     * the audit log silently loses the close event for a real impersonation
     * session.
     */
    reportSsoEmbedClosed() {
        const embed = this.state.ssoEmbed;
        if (!embed) return;
        this.data.reportSsoEvent(this.data.aba, embed.clientId, SSO_EVENTS.SESSION_CLOSED, embed.boot?.sessionId);
    }

    /**
     * Run the confirmed action. The half that genuinely differs per page: this
     * one embeds the consumer app in the detail panel and reloads a table;
     * /users/user opens a tab and refetches one member.
     * @param {'sso'|'restrict'} action
     * @param {string} clientId
     * @returns {Promise<boolean>} false leaves the confirmation open.
     */
    async runUserAction(action, clientId) {
        if (action === USER_ACTIONS.SSO) return this.performOpenSso(clientId);
        // Always the restrict direction: restore never opens this modal, so
        // the member is by definition still unrestricted here.
        return this.performRestrict(clientId, false);
    }

    /**
     * Resolve one SSO session and mount the embedded consumer app in the panel.
     * @returns {Promise<boolean>} false on failure, so the modal stays open.
     */
    async performOpenSso(clientId) {
        this.setSsoLoading(clientId, true);
        try {
            const boot = await resolveSsoBoot(clientId);
            this.setState({ ssoEmbed: { clientId, boot } });
            return true;
        }
        catch (error) {
            console.error('Open SSO failed:', error);
            message.error(ssoErrorMessage(error));
            return false;
        }
        finally {
            this.setSsoLoading(clientId, false);
        }
    }

    /**
     * Ask before cutting off a member's access. Replaces the row-level
     * Popconfirm, so the row, the detail panel and /users/user all confirm the
     * same way with the same copy.
     *
     * Restoring does NOT confirm (KAN-579 §2): it is the undo for the action
     * that just asked, and a second dialog in front of the way out only keeps a
     * member locked out longer. It fires straight through to performRestrict.
     * @param {string} clientId
     * @param {boolean} isRestricted — current restriction state.
     */
    restrictUser(clientId, isRestricted = false) {
        if (!clientId) return;

        if (isRestricted) {
            this.performRestrict(clientId, isRestricted);
            return;
        }

        this.requestUserAction(USER_ACTIONS.RESTRICT, clientId, {
            name: this.getUserById(clientId)?.name,
        });
    }

    /**
     * Restrict (or restore) a user's access to the platform. The endpoint
     * idempotently sets `account.active`, so both directions share one call
     * shape. Both are live from the UI: restricting arrives here through the
     * confirmation, restoring straight from `restrictUser` on a single click.
     * @param {string} clientId
     * @param {boolean} isRestricted — current restriction state; passed through
     *   as the `active` flag to the API (`true` restores, `false` restricts).
     * @returns {Promise<boolean>} false on failure, so the modal stays open.
     */
    async performRestrict(clientId, isRestricted = false) {
        const active = isRestricted;

        this.setActionLoading(clientId, 'restrict', true);
        try {
            const result = await this.data.restrictUser(clientId, active);
            this.setUserRestricted(clientId, result?.active === false);
            this.loadSyncDetailForUser(clientId, { force: true });
            await this.loadUsers();
            message.success(active
                ? 'User access restored.'
                : 'User access restricted.');
            return true;
        }
        catch (error) {
            console.error('Error restricting user:', error);
            const status = error?.response?.status;
            if (status === 403) message.error('You do not have access to do that for this FI.');
            else if (status === 404) message.error('User not found.');
            else message.error('Failed to update user access. Please try again.');
            return false;
        }
        finally {
            this.setActionLoading(clientId, 'restrict', false);
        }
    }

    /**
     * Delete the user's most recent (non-DD-switch) sync row, then refetch
     * the detail report; it carries the new latest row's state, which also
     * patches the row's bulk pill — no page refetch for one cell.
     * @param {string} clientId
     */
    async deleteLatestSync(clientId) {
        if (!clientId) return;

        this.setActionLoading(clientId, 'deleteSync', true);
        try {
            await this.data.deleteUserLatestSync(clientId);
            await this.confirmSyncAction(clientId, 'Latest sync row deleted.');
        }
        catch (error) {
            console.error('Error deleting latest sync:', error);
            const status = error?.response?.status;
            if (status === 403) message.error('You do not have access to do that for this FI.');
            else if (status === 404) message.error('No sync row to delete.');
            else message.error('Failed to delete sync row. Please try again.');
        }
        finally {
            this.setActionLoading(clientId, 'deleteSync', false);
        }
    }

    /**
     * Force-mark the user's most recent (non-DD-switch) sync row as COMPLETE,
     * then refetch the detail report; it patches the row's bulk pill too.
     * @param {string} clientId
     */
    async markLatestSyncComplete(clientId) {
        if (!clientId) return;

        this.setActionLoading(clientId, 'markComplete', true);
        try {
            await this.data.markUserLatestSyncComplete(clientId);
            await this.confirmSyncAction(clientId, 'Sync row marked complete.');
        }
        catch (error) {
            console.error('Error marking sync complete:', error);
            const status = error?.response?.status;
            if (status === 403) message.error('You do not have access to do that for this FI.');
            else if (status === 404) message.error('No sync row to mark complete.');
            else message.error('Failed to mark sync row complete. Please try again.');
        }
        finally {
            this.setActionLoading(clientId, 'markComplete', false);
        }
    }

    /**
     * `ClockoutController.componentWillUnmount` invokes `this.cleanup()`,
     * so this is where we unsubscribe the `matchMedia` listener and drop any
     * export poll timer to avoid leaks when the page unmounts. Also reports
     * `session_closed` for any still-embedded SSO app — navigating off Reports
     * is a teardown path too.
     */
    async cleanup() {
        this.reportSsoEmbedClosed();
        this._unmounted = true;
        this.unmountResponsive();
        this.stopExportPolling({ dismiss: true });
    }
}

export default ReportsController;
